import * as Sentry from '@sentry/react';
import { getLastDomGuardTelemetry } from './domMutationGuards';
import { environment } from '../../environments/environment';
import { isRtkQueryPlainObjectRejection } from './reduxApiSentry';

const DOM_NOT_FOUND_MESSAGE_FRAGMENTS = [
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
];

const isDomNotFoundError = (error: unknown): error is DOMException => {
  return (
    error instanceof DOMException &&
    error.name === 'NotFoundError' &&
    DOM_NOT_FOUND_MESSAGE_FRAGMENTS.some((fragment) => error.message.includes(fragment))
  );
};

const isDomNotFoundEvent = (event: Sentry.Event, hint?: Sentry.EventHint): boolean => {
  const originalException = hint?.originalException;

  if (isDomNotFoundError(originalException)) {
    return true;
  }

  const values = event.exception?.values ?? [];
  return values.some(
    (value) =>
      value.type === 'NotFoundError' &&
      DOM_NOT_FOUND_MESSAGE_FRAGMENTS.some((fragment) => value.value?.includes(fragment))
  );
};

const getDomOperation = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
): 'removeChild' | 'insertBefore' | 'unknown' => {
  const originalException = hint?.originalException;
  const message =
    (originalException instanceof Error ? originalException.message : undefined) ??
    event.exception?.values?.[0]?.value ??
    event.message ??
    '';

  if (message.includes('removeChild')) return 'removeChild';
  if (message.includes('insertBefore')) return 'insertBefore';
  return 'unknown';
};

export const getSentryEnvironment = () => environment.sentry.environment;

export const initSentry = () => {
  const sentryEnvironment = getSentryEnvironment();
  const isProduction = sentryEnvironment === 'production';
  const {
    dsn,
    release,
    dist,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    profilingEnabled,
    profileSessionSampleRate,
    profileLifecycle,
  } = environment.sentry;
  const { buildId, cacheStrategy, isDevelopment } = environment.app;

  if (!dsn) {
    if (!isProduction) {
      console.info('Sentry deshabilitado: VITE_SENTRY_DSN no está configurado.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: sentryEnvironment,
    release,
    dist,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(profilingEnabled && profileSessionSampleRate > 0
        ? [Sentry.browserProfilingIntegration()]
        : []),
      Sentry.replayIntegration(),
      ...(isProduction
        ? []
        : [
            Sentry.consoleLoggingIntegration({
              levels: ['log', 'warn', 'error'],
            }),
          ]),
    ],
    tracesSampleRate,
    profileSessionSampleRate,
    profileLifecycle,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    enableLogs: !isProduction,
    beforeSend(event, hint) {
      if (isRtkQueryPlainObjectRejection(hint?.originalException)) {
        return null;
      }

      if (!isDomNotFoundEvent(event, hint)) {
        return event;
      }

      const domGuardTelemetry = getLastDomGuardTelemetry();
      const operation = getDomOperation(event, hint);
      const hasExternalSignals = !!(
        domGuardTelemetry?.signals.hasGoogleTranslateBanner ||
        domGuardTelemetry?.signals.hasGoogleTranslateFrame ||
        domGuardTelemetry?.signals.hasTranslatedClass
      );

      event.level = hasExternalSignals ? 'warning' : (event.level ?? 'error');
      event.tags = {
        ...event.tags,
        dom_not_found: 'true',
        dom_operation: operation,
        dom_guard_triggered: String(!!domGuardTelemetry),
        dom_external_mutation_suspected: String(hasExternalSignals),
      };
      event.fingerprint = [
        'dom-not-found',
        operation,
        hasExternalSignals ? 'external-signals' : 'no-signals',
      ];
      event.contexts = {
        ...event.contexts,
        dom_guard: {
          telemetry: domGuardTelemetry ?? null,
        },
      };

      return event;
    },
  });

  if (buildId) {
    Sentry.setTag('app.build_id', buildId);
  }

  if (cacheStrategy) {
    Sentry.setTag('app.cache_strategy', cacheStrategy);
  }

  Sentry.setContext('frontend_build', {
    release: release ?? 'not-set',
    dist: dist ?? 'not-set',
    buildId: buildId ?? 'not-set',
    cacheStrategy: cacheStrategy ?? 'not-set',
    environment: sentryEnvironment,
  });

  Sentry.setContext('sentry_sampling', {
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    profilingEnabled,
    profileSessionSampleRate,
    profileLifecycle,
    isDevelopment,
  });
};
